import { useEffect, useMemo } from 'react';
import { throttle } from 'lodash';
import { NetworkStatus } from '@apollo/client';
import {
  GetOpeningApplicantCardsQuery,
  GetOpeningApplicantCardsQueryVariables,
  useGetOpeningApplicantCardsLazyQuery,
  useSessionQuery,
} from '@src/graphql/generated';
import { useGetOpeningId } from '@src/views/Opening/hooks/useGetOpeningId';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import { handleFetchMore } from '@src/util/handleFetchMore';
import {
  UseGetOpeningApplicantsParams,
  UseGetOpeningApplicantsValue,
} from './interfaces/useGetOpeningApplicants.interface';

export const useGetOpeningApplicants = ({
  sources,
  recruiterMessageThreadStates,
  recruiterStates,
  userStates,
  firstBatchSize,
  nextBatchSize,
  sortBy,
  fetchPolicy,
}: UseGetOpeningApplicantsParams): UseGetOpeningApplicantsValue => {
  const openingId = useGetOpeningId();

  const { data: sessionData } = useSessionQuery();

  const recruiter = sessionData?.currentRecruiter;

  const [
    fetchOpeningApplicantCards,
    {
      data: openingApplicantsData,
      loading: isOpeningApplicantsLoading,
      fetchMore: fetchMoreOpeningOpeningApplicants,
      refetch: refetchOpeningOpeningApplicants,
      networkStatus: openingOpeningApplicantsNetworkStatus,
    },
  ] = useGetOpeningApplicantCardsLazyQuery({
    fetchPolicy: fetchPolicy,
    notifyOnNetworkStatusChange: true,
  });

  const openingApplicants = openingApplicantsData?.opening?.applicants.result || [];
  const openingApplicantsPagination = openingApplicantsData?.opening?.applicants.pagination;

  const isOpeningApplicantsInitialLoading = [NetworkStatus.loading].includes(openingOpeningApplicantsNetworkStatus);

  const openingApplicantsVariables = useMemo<GetOpeningApplicantCardsQueryVariables | undefined>(() => {
    if (!openingId) {
      return undefined;
    }

    return {
      filterInput: {
        recruiterMessageThreadStates: recruiterMessageThreadStates,
        recruiterStates: recruiterStates,
        sources: sources,
        userStates: userStates,
      },
      openingId: openingId,
      paginationOptions: {
        limit: firstBatchSize,
        sortBy: sortBy,
      },
      recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
    };
  }, [openingId, recruiter?.country?.currency, sortBy]);

  const loadMoreOpeningApplicants = throttle(() => {
    if (!isOpeningApplicantsLoading && openingApplicantsPagination?.hasMoreResults) {
      handleFetchMore(fetchMoreOpeningOpeningApplicants, {
        updateQuery: (
          previousResult: GetOpeningApplicantCardsQuery,
          { fetchMoreResult }: { fetchMoreResult: GetOpeningApplicantCardsQuery },
        ): GetOpeningApplicantCardsQuery => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          const previousApplicants = previousResult.opening?.applicants;

          const newResult = fetchMoreResult.opening?.applicants.result;
          const pagination = fetchMoreResult.opening?.applicants.pagination;

          return {
            ...previousResult,
            opening: {
              ...previousResult.opening,
              applicants: {
                ...previousResult.opening.applicants,
                pagination: {
                  ...previousApplicants.pagination,
                  hasMoreResults: pagination.hasMoreResults,
                  limit: pagination.limit,
                  maxCursor: pagination.maxCursor,
                },
                result: [...previousApplicants.result, ...newResult],
              },
            },
          };
        },
        variables: {
          paginationOptions: {
            limit: nextBatchSize,
            minCursor: openingApplicantsPagination.maxCursor,
            sortBy: sortBy,
          },
        },
      });
    }
  }, 200);

  const handleRefetchOpeningApplicants = async () => {
    await refetchOpeningOpeningApplicants(openingApplicantsVariables);
  };

  useEffect(() => {
    if (!openingApplicantsVariables) {
      return;
    }

    if (!openingApplicantsData) {
      fetchOpeningApplicantCards({
        variables: openingApplicantsVariables,
      });

      return;
    }

    refetchOpeningOpeningApplicants(openingApplicantsVariables);
  }, [openingApplicantsVariables]);

  return {
    isOpeningApplicantsInitialLoading: isOpeningApplicantsInitialLoading,
    isOpeningApplicantsLoading: isOpeningApplicantsLoading,
    loadMoreOpeningApplicants: loadMoreOpeningApplicants,
    openingApplicants: openingApplicants,
    openingApplicantsPagination: openingApplicantsPagination,
    refetchOpeningApplicants: handleRefetchOpeningApplicants,
  };
};
